import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { ShellProvider } from "@/ui/foundation/primitives/shell";
import { TooltipProvider } from "@/ui/foundation/primitives/tooltip";
import type { UiBridgeV2, UiCommandBusV2, UiCommandResult, UiManagerMainRoute, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { InfoIcon } from "lucide-react";
import { ManagerLayout } from "@/ui/components/manager/manager-layout";
import { managerScreenRegistry, useManagerScreenStateBag } from "@/ui/screens/manager/screen-registry";
import { IntroSelectionOverlay, useIntroOverlayState } from "@/ui/screens/manager/overlays/intro-selection-overlay";
import { ResultOverlay } from "@/ui/screens/manager/overlays/result-overlay";
import { DaySummaryOverlay } from "@/ui/screens/manager/overlays/day-summary-overlay";

type ThemeMode = "system" | "light" | "dark";
let sessionThemeMode: ThemeMode = "system";

function applyTheme(dark: boolean): void {
    const root = document.documentElement;
    root.classList.toggle("dark", dark);
}

interface ManagerUiAppProps {
    bridge: UiBridgeV2;
}

export function ManagerUiApp({ bridge }: ManagerUiAppProps) {
    const [snapshot, setSnapshot] = useState<UiSnapshotV2>(bridge.getSnapshot());
    const [optimisticRoute, setOptimisticRoute] = useState<UiManagerMainRoute | null>(null);
    const [themeMode, setThemeMode] = useState<ThemeMode>(sessionThemeMode);
    const [prefersDark, setPrefersDark] = useState<boolean>(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
    const [commandMessage, setCommandMessage] = useState<string>("");
    const [teamModalOpen, setTeamModalOpen] = useState<boolean>(false);

    const commands = useMemo<UiCommandBusV2>(() => {
        const target = bridge.commands;
        return new Proxy(target, {
            get(originalTarget, property, receiver) {
                const value = Reflect.get(originalTarget, property, receiver);
                if (typeof value !== "function") {
                    return value;
                }
                return async (...args: unknown[]): Promise<UiCommandResult> => {
                    if (property === "navigate") {
                        const requestedRoute = args[0];
                        if (typeof requestedRoute === "string") {
                            setOptimisticRoute(requestedRoute as UiManagerMainRoute);
                        }
                    }
                    const result = await value.apply(originalTarget, args);
                    if (result && typeof result.reason === "string" && result.reason.length > 0) {
                        setCommandMessage(result.reason);
                    }
                    return result;
                };
            },
        }) as UiCommandBusV2;
    }, [bridge]);

    useEffect(() => bridge.subscribe(setSnapshot), [bridge]);
    useEffect(() => {
        if (optimisticRoute && snapshot.activeRoute === optimisticRoute) {
            setOptimisticRoute(null);
        }
    }, [optimisticRoute, snapshot.activeRoute]);
    useEffect(() => {
        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);

    const darkMode = themeMode === "system" ? prefersDark : themeMode === "dark";
    const effectiveSnapshot = optimisticRoute
        ? { ...snapshot, activeRoute: optimisticRoute }
        : snapshot;

    useEffect(() => {
        applyTheme(darkMode);
    }, [darkMode]);

    const ctaLabel = snapshot.routes.home.summary.ctaLabel.replace(/^Action:\s*/i, "").trim() || "Next Day";
    const headerMessage = commandMessage || snapshot.routes.home.summary.message;

    const handleToggleTheme = (): void => {
        setThemeMode((current) => {
            if (current === "system") {
                return prefersDark ? "light" : "dark";
            }
            return current === "dark" ? "light" : "dark";
        });
    };

    useEffect(() => {
        sessionThemeMode = themeMode;
    }, [themeMode]);

    const screenState = useManagerScreenStateBag(effectiveSnapshot);
    const introOverlayState = useIntroOverlayState(effectiveSnapshot);

    const activeScreen = managerScreenRegistry[effectiveSnapshot.activeRoute];
    const secondaryVisible = activeScreen.secondaryVisible;
    const secondaryPane = activeScreen.renderSecondary({ snapshot: effectiveSnapshot, commands, state: screenState });
    const mainContent = activeScreen.renderMain({ snapshot: effectiveSnapshot, commands, state: screenState });

    const overlayContent = (
        <>
            <IntroSelectionOverlay
                snapshot={effectiveSnapshot}
                commands={commands}
                teamModalOpen={teamModalOpen}
                onCloseTeamModal={() => setTeamModalOpen(false)}
                state={introOverlayState}
            />
            <ResultOverlay snapshot={effectiveSnapshot} commands={commands} />
            <DaySummaryOverlay snapshot={effectiveSnapshot} commands={commands} />
        </>
    );

    return (
        <TooltipProvider delayDuration={0}>
            <div className="flex h-dvh min-h-dvh flex-col overflow-hidden bg-background text-foreground">
                <header className="sticky top-0 z-40 grid h-14 shrink-0 grid-cols-[auto_1fr_auto] items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-backdrop-filter:bg-background/80">
                    <div className="text-base font-semibold tracking-tight">TeamSquad</div>
                    <div className="min-w-0">
                        {headerMessage ? (
                            <Badge
                                variant="secondary"
                                className="mx-auto flex w-fit max-w-full items-center gap-1.5 truncate px-3 py-1 text-xs font-medium"
                            >
                                <InfoIcon className="size-3.5 shrink-0" />
                                <span className="truncate">{headerMessage}</span>
                            </Badge>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setTeamModalOpen(true)}>Team</Button>
                        <Button size="sm" onClick={() => void commands.nextDay()}>{ctaLabel}</Button>
                    </div>
                </header>
                <ShellProvider
                    defaultOpen
                    className="manager-shell-layout h-full min-h-0 flex-1 overflow-hidden"
                >
                    <ManagerLayout
                        snapshot={effectiveSnapshot}
                        darkMode={darkMode}
                        onToggleTheme={handleToggleTheme}
                        onNavigate={(route) => void commands.navigate(route)}
                        secondaryVisible={secondaryVisible}
                        secondaryPane={secondaryPane}
                        mainContent={mainContent}
                        overlayContent={overlayContent}
                    />
                </ShellProvider>
            </div>
        </TooltipProvider>
    );
}
